import {
  IDocumentCommentService,
  DOCUMENT_POLICY,
} from '../api/DocumentCommentService';
import {
  DocumentAttachment,
  RequestComment,
  DocumentCategory,
  EntityType,
} from '@/types/domain';
import { env } from '@/config/env';

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export class MockDocumentCommentService implements IDocumentCommentService {
  private documents: DocumentAttachment[] = [
    {
      id: 'doc-1001',
      requestId: 'req-1001',
      entityType: 'REQUEST',
      entityId: 'req-1001',
      fileName: 'NVIDIA_Workstation_Technical_Specification.pdf',
      originalName: 'NVIDIA_Workstation_Technical_Specification.pdf',
      fileSize: 4200000,
      fileType: 'application/pdf',
      extension: '.pdf',
      category: 'TechnicalSpec',
      uploadedBy: 'usr-1',
      uploadedByName: 'Sarah Jenkins',
      uploadedAt: '2026-09-01T10:30:00Z',
      url: '/mock-files/spec-1001.pdf',
      status: 'CLEAN',
      scanStatus: 'CLEAN',
      checksum: 'e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855',
    },
    {
      id: 'doc-1002',
      requestId: 'req-1001',
      entityType: 'REQUEST',
      entityId: 'req-1001',
      fileName: 'Vendor_Quotation_NVIDIA_Partner.pdf',
      originalName: 'Vendor_Quotation_NVIDIA_Partner.pdf',
      fileSize: 2800000,
      fileType: 'application/pdf',
      extension: '.pdf',
      category: 'Quotation',
      uploadedBy: 'usr-1',
      uploadedByName: 'Sarah Jenkins',
      uploadedAt: '2026-09-01T11:15:00Z',
      url: '/mock-files/quotation-1001.pdf',
      status: 'CLEAN',
      scanStatus: 'CLEAN',
      checksum: 'c2a5102a9e5b2938883e449a02298c8c2275466c40e530999557b4f5904ef233',
    },
  ];

  private comments: RequestComment[] = [
    {
      id: 'cmt-1001',
      requestId: 'req-1001',
      entityType: 'REQUEST',
      entityId: 'req-1001',
      userId: 'usr-2',
      userName: 'Marcus Vance',
      userRole: 'Department Manager',
      commentText: 'Technical specifications look solid. Confirming cost center allocation with Finance.',
      isClarification: false,
      attachmentIds: ['doc-1001'],
      createdAt: '2026-09-01T12:00:00Z',
      status: 'ACTIVE',
    },
  ];

  async getDocuments(entityType: EntityType, entityId: string): Promise<DocumentAttachment[]> {
    await delay(env.mockDelayMs);
    return this.documents.filter(
      (d) =>
        (d.entityType === entityType || !d.entityType) &&
        (d.entityId === entityId || d.requestId === entityId) &&
        d.status !== 'DELETED'
    );
  }

  async uploadDocument(
    entityType: EntityType,
    entityId: string,
    file: File,
    category: DocumentCategory
  ): Promise<DocumentAttachment> {
    await delay(env.mockDelayMs);

    // 1. Extension Validation
    const ext = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!DOCUMENT_POLICY.allowedExtensions.includes(ext as (typeof DOCUMENT_POLICY.allowedExtensions)[number])) {
      throw new Error(`Forbidden file extension ${ext}. Allowed: ${DOCUMENT_POLICY.allowedExtensions.join(', ')}`);
    }

    // 2. Single File Size Validation (10MB Limit)
    const maxBytes = DOCUMENT_POLICY.maxFileSizeMb * 1024 * 1024;
    if (file.size > maxBytes) {
      throw new Error(`File size (${(file.size / (1024 * 1024)).toFixed(2)} MB) exceeds maximum 10 MB limit.`);
    }

    // 3. Combined Total Request Size Validation (50MB Limit)
    const existing = await this.getDocuments(entityType, entityId);
    const currentTotalBytes = existing.reduce((acc, d) => acc + d.fileSize, 0);
    const maxTotalBytes = DOCUMENT_POLICY.maxTotalRequestSizeMb * 1024 * 1024;
    if (currentTotalBytes + file.size > maxTotalBytes) {
      throw new Error(
        `Total attachments size (${((currentTotalBytes + file.size) / (1024 * 1024)).toFixed(
          2
        )} MB) exceeds request maximum 50 MB limit.`
      );
    }

    // 4. File Count Validation (10 Files Limit)
    if (existing.length >= DOCUMENT_POLICY.maxFilesPerRequest) {
      throw new Error(`Maximum limit of ${DOCUMENT_POLICY.maxFilesPerRequest} attachments per request reached.`);
    }

    // 5. Mock Malware Scanning State Machine
    let scanResult: 'CLEAN' | 'INFECTED' | 'FAILED' = 'CLEAN';
    if (file.name.toLowerCase().includes('infected') || file.name.toLowerCase().includes('virus')) {
      scanResult = 'INFECTED';
    } else if (file.name.toLowerCase().includes('corrupt') || file.name.toLowerCase().includes('scan-fail')) {
      scanResult = 'FAILED';
    }

    const docStatus = scanResult === 'CLEAN' ? 'CLEAN' : scanResult === 'INFECTED' ? 'INFECTED' : 'SCAN_FAILED';

    const newDoc: DocumentAttachment = {
      id: `doc-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
      requestId: entityId,
      entityType,
      entityId,
      fileName: file.name,
      originalName: file.name,
      fileSize: file.size,
      fileType: file.type || 'application/octet-stream',
      extension: ext,
      category,
      uploadedBy: 'usr-1',
      uploadedByName: 'Sarah Jenkins',
      uploadedAt: new Date().toISOString(),
      url: `/mock-files/${file.name}`,
      status: docStatus,
      scanStatus: scanResult,
      checksum: `sha256-${Math.random().toString(36).substring(2, 10)}`,
    };

    if (scanResult === 'INFECTED') {
      newDoc.status = 'QUARANTINED';
      this.documents.push(newDoc);
      throw new Error(`Security Violation: File ${file.name} failed malware scan and has been quarantined.`);
    }

    this.documents.push(newDoc);
    return newDoc;
  }

  async downloadDocument(documentId: string): Promise<{ downloadUrl: string; fileName: string }> {
    await delay(env.mockDelayMs);
    const doc = this.documents.find((d) => d.id === documentId);
    if (!doc || doc.status === 'DELETED') {
      throw new Error('404 Document Not Found');
    }
    if (doc.status === 'INFECTED' || doc.status === 'QUARANTINED' || doc.scanStatus === 'FAILED') {
      throw new Error('403 Security Restriction: Cannot download quarantined or unscanned document.');
    }
    return {
      downloadUrl: doc.url,
      fileName: doc.fileName,
    };
  }

  async deleteDocument(documentId: string): Promise<boolean> {
    await delay(env.mockDelayMs);
    const doc = this.documents.find((d) => d.id === documentId);
    if (doc) {
      doc.status = 'DELETED';
      return true;
    }
    return false;
  }

  async getComments(entityType: EntityType, entityId: string): Promise<RequestComment[]> {
    await delay(env.mockDelayMs);
    return this.comments.filter(
      (c) =>
        (c.entityType === entityType || !c.entityType) &&
        (c.entityId === entityId || c.requestId === entityId) &&
        c.status !== 'DELETED'
    );
  }

  async createComment(
    entityType: EntityType,
    entityId: string,
    commentText: string,
    attachmentIds: string[] = [],
    isClarification = false
  ): Promise<RequestComment> {
    await delay(env.mockDelayMs);

    const trimmed = commentText.trim();
    if (!trimmed) {
      throw new Error('Comment cannot be empty or whitespace only.');
    }
    if (trimmed.length > DOCUMENT_POLICY.commentMaxLength) {
      throw new Error(`Comment length (${trimmed.length}) exceeds maximum limit of ${DOCUMENT_POLICY.commentMaxLength} characters.`);
    }

    // XSS Sanitization (Escaping HTML script tags)
    const sanitizedText = trimmed
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#x27;');

    const newComment: RequestComment = {
      id: `cmt-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
      requestId: entityId,
      entityType,
      entityId,
      userId: 'usr-1',
      userName: 'Sarah Jenkins',
      userRole: 'Requester / Employee',
      commentText: sanitizedText,
      isClarification,
      attachmentIds,
      attachmentId: attachmentIds[0],
      createdAt: new Date().toISOString(),
      status: 'ACTIVE',
    };

    this.comments.push(newComment);
    return newComment;
  }

  async updateComment(commentId: string, commentText: string): Promise<RequestComment> {
    await delay(env.mockDelayMs);
    const cmt = this.comments.find((c) => c.id === commentId);
    if (!cmt || cmt.status === 'DELETED') {
      throw new Error('Comment not found');
    }
    cmt.commentText = commentText;
    cmt.updatedAt = new Date().toISOString();
    cmt.status = 'EDITED';
    return cmt;
  }

  async deleteComment(commentId: string): Promise<boolean> {
    await delay(env.mockDelayMs);
    const cmt = this.comments.find((c) => c.id === commentId);
    if (cmt) {
      cmt.status = 'DELETED';
      return true;
    }
    return false;
  }
}

export const mockDocumentCommentService = new MockDocumentCommentService();
